<template>
  <div class="wiki-editor-container" :class="[outerClass, { 'read-only': readOnly }]" :data-shared-edit="isEditModeShared || undefined">
    <slot name="topLeftContent"/>
    <slot name="readOnlyMessage"/>
    <div :class="editorWrapperClass">
      <div
        :class="['focus-border-wrapper', { 'has-focus': isFocused && !isEditMode }]"
      >
        <div
          ref="surfaceRef"
          :class="surfaceClass"
          :tabindex="isMarkdown ? undefined : readOnly ? -1 : (needsHiddenEditButton && !isEditMode ? undefined : 0)"
          :role="!isMarkdown && !readOnly && !isEditMode && !hideControls ? 'button' : undefined"
          :aria-label="!isMarkdown && !readOnly && !isEditMode && !hideControls ? viewModeAriaLabel : undefined"
          @click="enterEditModeIfNoSelection"
          @copy="copyWithoutScreenReaderText"
          @keydown.enter="enterEditModeByKeyboard"
          @keydown.space="enterEditModeByKeyboard"
          @keydown.esc="finishEditingByKeyboard"
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false"
          @focus="isFocused = true"
          @blur="isFocused = false"
        >
          <button
            v-if="needsHiddenEditButton && !isEditMode"
            ref="hiddenEditButtonRef"
            type="button"
            class="sr-only focus:not-sr-only"
            :aria-label="hiddenEditButtonLabel"
            @click.stop="enterEditMode"
          >
            Edit
          </button>
          <template v-if="isMarkdown">
            <WikiTextArea
              v-if="isEditMode"
              v-bind="nameAttrs"
              class="fs-exclude"
              :text="wikiText"
              @draft="onDraft"
              @blurred="onBlurred"
            />
            <div v-else class="fs-exclude">
              <WikiText v-if="wikiText" :text="wikiText" :strategies="strategies" :resources="resources" />
              <i v-else-if="placeHolder" class="text-gray-700">{{ placeHolder }}</i>
            </div>
          </template>
          <TiptapWikiTextBody
            v-else
            ref="tiptapBodyRef"
            :wiki-text="wikiText"
            :is-edit-mode="isEditMode"
            :place-holder="placeHolder"
            :name-attrs="nameAttrs"
            :to-embedded-text="toEmbeddedText"
            :to-wiki-text="toWikiText"
            @draft="onDraft"
            @blurred="onBlurred"
          />
          <div v-if="isHintShown" class="edit-tooltip" aria-hidden="true">{{ hintText }}</div>
        </div>
      </div>
      <slot name="innerContent"/>
    </div>
    <div class="mt-4"><slot name="innerBottomContent"/></div>
    <div class="edit-button-container flex flex-wrap justify-between mt-4">
      <div class="flex items-center gap-2">
        <WikiTextEditorControls
          v-if="!hideControls"
          ref="controlsRef"
          :isEditMode="isEditMode"
          :textToCopy="textToCopy"
          :copy-tooltip-text="copyTooltipText"
          :content-label="controlsContentLabel"
          :read-only="readOnly"
          :show-copied-check="showCopiedCheck"
          @toggle="toggleEditMode"
          @mousedown="keepEditorFocused"
        />
        <GbButton v-if="canReset" gbTheme="grayOutlineSecondary" size="xs" class="bg-white self-end -mb-px" :aria-label="`Reset ${label}`" @click="resetText">
          <span class="material-icons-round pr-1 text-lg" aria-hidden="true">undo</span>Reset
        </GbButton>
        <slot name="bottomLeftContent"/>
      </div>
      <slot name="bottomRightContent"/>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import WikiTextEditorControls from '@/components/editor/WikiTextEditorControls.vue'
import TiptapWikiTextBody from './TiptapWikiTextBody.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import WikiTextArea from '@/components/wikitext/WikiTextArea.vue'
import GbButton from '@/components/GbButton.vue'
import { toEditableText } from '@/components/wikitext/editable_wiki_text'
import { getSelection, getSelectedText, removeScreenReaderText } from '@/util/copypaste_helpers'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import type { AnalyticsData } from '@/tracking/analytics/analyticsTracker'
import type { Resource } from '@/resources/resource_types'
import type { WikiTextStrategy } from '@/components/wikitext/wiki_text_types'

const EDIT_HINT = 'Click anywhere to begin editing'
const RESET_CONFIRMATION = 'Are you sure you want to discard all changes?'
const DEFAULT_LABEL = 'Content text editor'

// One editor for both kinds of stored wiki text. 'tiptap-html' is the HTML Present Levels and
// Impact of Disability store, displayed and edited in Tiptap. 'markdown' is markdown-flavoured wiki
// text (strategy notes), displayed with WikiText and edited as plain text in WikiTextArea.
const props = withDefaults(defineProps<{
  wikiText?: string
  format?: 'tiptap-html' | 'markdown'
  label?: string
  labelledBy?: string
  placeHolder?: string
  outerClass?: string
  editorWrapperClass?: string
  toEmbeddedText?: (input: string) => string
  toWikiText?: (output: string) => string
  resetValue?: string
  showReset?: boolean
  editedTextType?: string
  readOnly?: boolean
  readOnlyHint?: string
  hideControls?: boolean
  strategies?: Record<string, WikiTextStrategy>
  resources?: Record<string, Resource>
  evtData?: AnalyticsData
  editMode?: boolean
  copyTooltipText?: string
  showCopiedCheck?: boolean
}>(), {
  wikiText: '',
  format: 'tiptap-html',
  label: DEFAULT_LABEL,
  placeHolder: '',
  outerClass: '',
  editorWrapperClass: '',
  toEmbeddedText: (input: string) => input,
  toWikiText: (output: string) => output,
  strategies: () => ({}),
  resources: () => ({}),
  evtData: () => ({}),
  editMode: undefined,
  copyTooltipText: undefined,
})

const emit = defineEmits<{
  'update:wikiText': [wikiText: string]
  'editModeUpdated': [isEditMode: boolean]
  'update:editMode': [isEditMode: boolean]
}>()

const isMarkdown = props.format === 'markdown'

// The Tiptap editor has always tagged its events with edited_text_type, empty when unset; the
// markdown editor, like Vue 2's, never has.
const tracker = getAnalyticsTracker()
const analytics = isMarkdown ? tracker : tracker.getChildContext({ edited_text_type: props.editedTextType ?? '' })

const surfaceRef = ref<HTMLElement | null>(null)
const hiddenEditButtonRef = ref<HTMLButtonElement | null>(null)
const controlsRef = ref<InstanceType<typeof WikiTextEditorControls> | null>(null)
const tiptapBodyRef = ref<InstanceType<typeof TiptapWikiTextBody> | null>(null)
const ownEditMode = ref(false)
// A page editing several statements together (as one "Edit" for all of them) passes editMode and
// owns the mode, and its analytics, through update:editMode; otherwise each editor owns its own.
const isEditModeShared = computed(() => props.editMode !== undefined)
const isEditMode = computed(() => props.editMode ?? ownEditMode.value)
const isHovering = ref(false)
const isFocused = ref(false)
const isHintDismissed = ref(false)
const draftText = ref('')
const draftTextAtEditStart = ref('')
const draftValue = ref('')
const valueAtEditStart = ref('')

const editor = computed(() => tiptapBodyRef.value?.editor)
const controlsElement = () => controlsRef.value?.$el as HTMLElement | undefined
const nameAttrs = computed((): Record<string, string> =>
  props.labelledBy ? { 'aria-labelledby': props.labelledBy } : { 'aria-label': props.label }
)
const surfaceClass = computed(() => isMarkdown
  ? ['group relative overflow-hidden [overflow-wrap:anywhere] border border-transparent p-px', props.readOnly ? 'hover:cursor-not-allowed hover:border-gray-400 hover:bg-gray-100' : 'hover:border-yellow-200 hover:bg-yellow-100']
  : ['rounded min-h-24 font-default relative overflow-hidden fs-exclude', { 'edit-mode': isEditMode.value, 'cursor-not-allowed': props.readOnly }])
const copyText = computed(() => isMarkdown ? toEditableText(props.wikiText) : tiptapBodyRef.value?.getPlainText() ?? '')
const textToCopy = computed(() => isEditMode.value ? draftText.value : copyText.value)
// The markdown editor names its Edit, Done and Copy buttons after a caller's own label, as the
// statement editor it replaces did ("Edit 1st Quarter: Counting statement").
const controlsContentLabel = computed(() => isMarkdown && props.label !== DEFAULT_LABEL ? props.label : '')
const hintText = computed(() => props.readOnly ? props.readOnlyHint : EDIT_HINT)
// Disambiguates the pre-edit surface's own name when a caller passes a per-instance label (e.g.
// one impact statement among several) -- the generic default stays plain so unlabelled callers
// are unaffected (WCAG 2.4.6, buttons-confusing).
const viewModeAriaLabel = computed(() => props.label !== DEFAULT_LABEL ? `${props.label}. ${EDIT_HINT}.` : EDIT_HINT)
// WCAG 4.1.2 (nested-interactive): a resource citation renders as a real focusable button
// (ResourceNodeComponent) inside the tiptap content regardless of hideControls, so the surface
// must never carry button semantics itself. When the toolbar's Edit button is hidden, this
// sr-only-focusable button is the keyboard/AT way in instead; mouse users keep "click anywhere".
const needsHiddenEditButton = computed(() => !isMarkdown && !props.readOnly && props.hideControls)
const hiddenEditButtonLabel = computed(() => props.label !== DEFAULT_LABEL ? `Edit ${props.label}` : 'Edit')
const isHintShown = computed(() => (isHovering.value || isFocused.value) && !isEditMode.value && !isHintDismissed.value && !!hintText.value)
const canReset = computed(() =>
  props.showReset && !props.readOnly && !isEditMode.value && props.resetValue !== undefined && props.wikiText !== props.resetValue
)

const isTextEdited = () => {
  if (isMarkdown) return props.wikiText !== (props.resetValue ?? valueAtEditStart.value)
  const resetValue = props.resetValue ?? ''
  if (!resetValue && !editor.value?.getText()) return false
  return resetValue !== editor.value?.getHTML()
}

// Markdown: an untouched draft must not save, because its plain text has already dropped
// formatting the textarea cannot show. Tiptap HTML: the statement pages save on every
// update:wikiText and always have, so every edit that ends emits the editor's text. A shared edit
// can save more than once before it ends, so each save becomes the draft's new starting point.
const commit = () => {
  if (isMarkdown && (draftText.value === draftTextAtEditStart.value || draftValue.value === props.wikiText)) return
  emit('update:wikiText', draftValue.value)
  draftTextAtEditStart.value = draftText.value
}

const startDraft = () => {
  valueAtEditStart.value = props.wikiText
  draftValue.value = props.wikiText
  draftText.value = copyText.value
  draftTextAtEditStart.value = copyText.value
}

// Every shared editor opens together, so the one that was clicked takes focus back once they have.
const enterSharedEditMode = () => {
  emit('update:editMode', true)
  nextTick(() => surfaceRef.value?.querySelector('textarea')?.focus())
}

const enterEditMode = () => {
  if (props.readOnly || isEditMode.value) return
  if (isEditModeShared.value) return enterSharedEditMode()
  startDraft()
  ownEditMode.value = true
  analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.ACTIVATE_EDIT, props.evtData)
  emit('editModeUpdated', true)
}

// The page can also end a shared edit itself (its own Done), which no blur of this editor saw.
watch(() => props.editMode, (isSharedEditOn, wasSharedEditOn) => {
  if (isSharedEditOn) return startDraft()
  if (wasSharedEditOn) commit()
}, { immediate: true })

// Wait a tick so the parent has applied the committed value before it is compared.
const exitEditMode = () => {
  if (isEditModeShared.value) return emit('update:editMode', false)
  ownEditMode.value = false
  emit('editModeUpdated', false)
  nextTick(() => analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.DEACTIVATE_EDIT, {
    is_text_edited: isTextEdited(),
    edited_text_text: props.wikiText,
    ...props.evtData
  }))
}

// Without the controls, the hidden Edit button is how a keyboard user got in, so focus goes back there.
const returnFocus = (options?: FocusOptions) => nextTick(() => (controlsElement()?.querySelector('button') ?? hiddenEditButtonRef.value ?? surfaceRef.value)?.focus(options))

// Clicking blank page space ends the edit with focus on <body>; the user didn't ask to move, so don't scroll.
const returnFocusIfLost = () => nextTick(() => {
  const isFocusLost = !document.activeElement || document.activeElement === document.body
  if (isFocusLost) returnFocus({ preventScroll: true })
})

const finishEditing = () => {
  if (!isEditMode.value) return
  commit()
  exitEditMode()
  returnFocus()
}

const toggleEditMode = () => isEditMode.value ? finishEditing() : enterEditMode()

const onDraft = (plainText: string, wikiText: string) => {
  draftText.value = plainText
  draftValue.value = wikiText
}

// A shared edit spans several editors' textareas and the page's own controls for it (marked
// data-shared-edit-control), so moving between them keeps it open.
const isStayingInSharedEdit = (relatedTarget: EventTarget | null) => {
  if (!isEditModeShared.value || !(relatedTarget instanceof Element)) return false
  const isAnotherEditor = relatedTarget instanceof HTMLTextAreaElement && Boolean(relatedTarget.closest('.wiki-editor-container[data-shared-edit]'))
  const isSharedEditControl = Boolean(relatedTarget.closest('[data-shared-edit-control]'))
  return isAnotherEditor || isSharedEditControl
}

// Focus moving to the toolbar (Copy, or Done, which finishes the edit itself) keeps the edit
// open; anywhere else ends it. A blur caused by finishing the edit is already handled.
const onBlurred = (relatedTarget: EventTarget | null) => {
  if (!isEditMode.value) return
  if (relatedTarget instanceof Node && controlsElement()?.contains(relatedTarget)) return
  commit()
  if (isStayingInSharedEdit(relatedTarget)) return
  exitEditMode()
  returnFocusIfLost()
}

// Pressing a toolbar control must not blur the editor while editing, or the edit would end
// before Copy reads the draft.
const keepEditorFocused = (event: MouseEvent) => {
  if (isEditMode.value) event.preventDefault()
}

// Selecting text to copy it, or following a link in markdown text, should not start editing.
const enterEditModeIfNoSelection = (event: MouseEvent) => {
  if (event.target instanceof Element && event.target.closest('a')) return
  if (getSelection()?.isCollapsed ?? true) enterEditMode()
}

// Markdown text holds links, so it is not focusable and the keyboard way in is Edit. The Tiptap
// statement stays focusable with Enter/Space to edit (WAI-ARIA button pattern) only when the
// toolbar's own Edit button is shown; when hideControls hides it, the sr-only Edit button above
// handles Enter/Space natively instead (WCAG 4.1.2, nested-interactive).
const enterEditModeByKeyboard = (event: KeyboardEvent) => {
  if (isMarkdown || isEditMode.value || props.hideControls) return
  event.preventDefault()
  enterEditMode()
}

const finishEditingByKeyboard = (event: KeyboardEvent) => {
  if (!isEditMode.value) return
  event.preventDefault()
  finishEditing()
}

const resetText = () => {
  if (!window.confirm(RESET_CONFIRMATION)) return
  emit('update:wikiText', props.resetValue ?? '')
  analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.RESET)
  returnFocus()
}

const copyWithoutScreenReaderText = (event: ClipboardEvent) => {
  if (isEditMode.value) return
  event.clipboardData?.setData('text/plain', removeScreenReaderText(getSelectedText()))
  event.preventDefault()
}

// WCAG 1.4.13 (Dismissible): the hint shows on hover or focus, so Escape has to be heard
// document-wide while it is showing, not only when the editor has focus.
const dismissHintOnEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') isHintDismissed.value = true
}

watch(() => isHovering.value || isFocused.value, isTriggered => {
  isHintDismissed.value = false
  if (isTriggered) document.addEventListener('keydown', dismissHintOnEscape)
  else document.removeEventListener('keydown', dismissHintOnEscape)
})

onBeforeUnmount(() => document.removeEventListener('keydown', dismissHintOnEscape))

defineExpose({ editor, focusControls: returnFocus })
</script>
<style lang="postcss">
.tiptap {
  @apply border rounded-md;
  border-color: #BFD9EF;
  background-color: #EFF6FF;
  p {
    @apply p-1;
  }
  h1 {
    @apply text-xl font-bold mb-2;
  }
  ul, ol {
    @apply pl-5 mb-4;
  }
  ul {
    @apply list-disc;
  }
  ol {
    @apply list-decimal;
  }
  li {
    @apply mb-1;
  }
  strong {
    @apply font-bold;
  }
  em {
    @apply italic;
  }

  .mention-view {
    @apply inline-flex items-center gap-1 bg-blue-100 text-blue-500 px-2 py-0.5 rounded-sm;

    .icon {
      @apply text-sm;
    }

    .label {
      @apply text-sm;
    }
  }
}

.content-editor .ProseMirror {
  @apply min-h-24;
}

.wiki-editor-container .edit-mode {
  .tiptap {
    @apply rounded-md bg-yellow-100 border border-blue-400 outline-blue-400 shadow-wiki-editor;
  }
}

.focus-border-wrapper {
  @apply rounded-md;
  margin: -4px;
  padding: 4px;
  border: 2px solid transparent;
  border-bottom: 0 !important;
}

.focus-border-wrapper.has-focus {
  border-color: #004D72;
}

.wiki-editor-container:not(.read-only) > div > div > div:hover:not(.edit-mode) .tiptap {
  border-color: #007DBA;
}

.edit-tooltip {
  @apply absolute bg-black text-white pointer-events-none;
  right: 0;
  bottom: 0;
  padding: 4px 16px;
  font-size: 12px;
  z-index: 10;
  transform: translateY(2rem);
  animation: slideUp 0.3s ease-out forwards;
  border-radius: 0 0 6px 0;
}

@keyframes slideUp {
  to {
    transform: translateY(0);
  }
}
.white-mode {
  .tiptap {
    background-color: transparent;
    border-color: transparent;
  }
}
</style>
